import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_LINKED_MEDIA } from '../../graphql';
import { useCopy } from '../../lib/copyToClipboard';

export interface LinkedImage {
  url: string;
  uses: number;
  articleIds: string[];
  latestTitle?: string | null;
  latestAt?: string | null;
}

// The design shows a page at a time with "Show 24 more" (App Prototype, Media).
const PAGE = 24;

/** The linked images for an account (search: address or article title), a page at a time. */
export function useLinkedMedia(accountId: string | null | undefined, search = '', limit = PAGE) {
  const { data, loading, error, fetchMore } = useQuery<{
    linkedMedia: { items: LinkedImage[]; total: number };
  }>(GET_LINKED_MEDIA, {
    variables: { accountId, limit, offset: 0, search: search.trim() || null },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const items = data?.linkedMedia.items || [];
  const total = data?.linkedMedia.total ?? 0;
  const more = () =>
    fetchMore({
      variables: { offset: items.length },
      updateQuery: (prev, { fetchMoreResult }) =>
        fetchMoreResult
          ? {
              linkedMedia: {
                ...fetchMoreResult.linkedMedia,
                items: [...prev.linkedMedia.items, ...fetchMoreResult.linkedMedia.items],
              },
            }
          : prev,
    });
  // settled: answered, or failed (so a count beside it needn't wait for ever).
  return { items, total, loading: loading && !data, settled: Boolean(data || error), more };
}

/** Feed titles can arrive with HTML entities ("NASA&#8217;s"): show the characters. */
function decodeEntities(text: string): string {
  return text
    .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCodePoint(parseInt(n, 16)))
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, '\u00a0')
    .replace(/&amp;/g, '&');
}

/** "montecarlopicks.com" when every image so far comes from one host, else null. */
function oneHost(items: LinkedImage[]): string | null {
  const hosts = new Set(
    items.map((m) => {
      try {
        return new URL(m.url).host.replace(/^www\./, '');
      } catch {
        return '';
      }
    })
  );
  return hosts.size === 1 ? Array.from(hosts)[0] || null : null;
}

/**
 * Content → Media → "Linked from your feeds" (App Prototype, Media; backlog
 * ebda8050): images the articles show by address — a feed's hero image — that
 * were never brought into Media. Listed, not copied: they don't count toward
 * storage, and they have no versions. Its count is its own; the Media tab
 * counts uploads only.
 */
export function LinkedMedia({ accountId, search }: { accountId: string; search: string }) {
  const { items, total, loading, more } = useLinkedMedia(accountId, search);
  const { copy, state } = useCopy();
  const [failed, setFailed] = useState<Set<string>>(new Set());
  if (loading || !total) return null;
  const host = oneHost(items);
  const left = total - items.length;
  return (
    <section
      className="ink-linked"
      aria-label="Linked from your feeds"
      data-screen-label="Media linked from feeds"
    >
      <div className="ink-linked-head">
        <h2 className="ink-linked-label">Linked from your feeds</h2>
        <span className="ink-linked-count">{total.toLocaleString()}</span>
      </div>
      <p className="ink-linked-desc">
        These stay on {host || 'the feed’s site'} and don’t use your storage, and they have no
        versions.
      </p>
      <div className="ink-media-grid">
        {items.map((m) => (
          <div key={m.url} className="ink-media-card">
            <div className="ink-media-thumb" data-linked="true">
              {failed.has(m.url) ? (
                <span className="ink-media-fallback">image unavailable</span>
              ) : (
                <img
                  src={m.url}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={() => setFailed((f) => new Set(f).add(m.url))}
                />
              )}
            </div>
            <div className="ink-linked-title" title={m.url}>
              {m.latestTitle ? decodeEntities(m.latestTitle) : m.url.split('/').pop() || m.url}
            </div>
            <div className="ink-media-meta">
              <span>
                Used in {m.uses.toLocaleString()} {m.uses === 1 ? 'article' : 'articles'}
              </span>
              <button
                type="button"
                className="ink-linked-copy"
                onClick={(e) => void copy(m.url, m.url, e)}
              >
                {state(m.url) === 'copied'
                  ? 'Copied'
                  : state(m.url) === 'failed'
                    ? 'Couldn’t copy'
                    : 'Copy address'}
              </button>
            </div>
          </div>
        ))}
      </div>
      {left > 0 ? (
        <div className="ink-linked-more-row">
          <button type="button" className="ink-btn" onClick={() => void more()}>
            Show {Math.min(PAGE, left).toLocaleString()} more
          </button>
        </div>
      ) : null}
      <div className="ink-media-end">
        <span />
        {left > 0
          ? `Showing ${items.length.toLocaleString()} of ${total.toLocaleString()}`
          : `That’s all ${total.toLocaleString()} linked ${total === 1 ? 'image' : 'images'}`}
        <span />
      </div>
    </section>
  );
}
